import React, { useMemo } from "react";
import { Button, Modal, Tooltip } from "antd";
import { CheckOutlined, DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";

import PayrollDataTable from "./PayrollDataTable";
import EmployeeAvatar from "./EmployeeAvatar";
import { buildEmployeeMap } from "../helpers/payrollDisplay.helper";

import type { PayrollTemplate } from "../types";

interface PayrollTemplatesTabProps {
  templates: PayrollTemplate[];
  loading: boolean;
  employees: any[];
  onApply: (template: PayrollTemplate) => void;
  onDelete: (template: PayrollTemplate) => void;
}

const summarizeConfig = (t: PayrollTemplate) => {
  const cfg = t.template_config || ({} as PayrollTemplate["template_config"]);
  const depts = cfg.department_ids?.length || 0;
  const emps = cfg.employee_ids?.length || 0;
  const exceptions = cfg.exceptions?.length || 0;
  const parts: string[] = [];
  parts.push(depts ? `${depts} dept${depts > 1 ? "s" : ""}` : "All depts");
  parts.push(emps ? `${emps} emp${emps > 1 ? "s" : ""}` : "All emps");
  parts.push(`${exceptions} exception${exceptions === 1 ? "" : "s"}`);
  return parts.join(" · ");
};

export const PayrollTemplatesTab: React.FC<PayrollTemplatesTabProps> = ({
  templates,
  loading,
  employees,
  onApply,
  onDelete,
}) => {
  const employeeMap = useMemo(() => buildEmployeeMap(employees), [employees]);

  const confirmDelete = (template: PayrollTemplate) => {
    Modal.confirm({
      title: "Delete template",
      content: `Delete "${template.template_name}"? This cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () => onDelete(template),
    });
  };

  const columns: ColumnsType<PayrollTemplate> = [
    {
      title: "Template",
      dataIndex: "template_name",
      key: "template_name",
      render: (v: string, record) => (
        <div>
          <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 13.5 }}>
            {v}
          </div>
          <div className="small" style={{ color: "#94a3b8" }}>
            {summarizeConfig(record)}
          </div>
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (v: string | null) => (
        <span style={{ fontSize: 13, color: "#475569" }}>{v || "—"}</span>
      ),
    },
    {
      title: "Created By",
      dataIndex: "created_by_name",
      key: "created_by_name",
      width: 200,
      render: (v: string | null, record) => {
        const emp = employeeMap.get(Number(record.created_by_user_id));
        const name = v || emp?.name || `User #${record.created_by_user_id}`;
        return (
          <div className="d-flex align-items-center gap-2">
            <EmployeeAvatar name={name} image={emp?.image} size={32} />
            <span style={{ fontSize: 13 }}>{name}</span>
          </div>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (v: string) => (
        <span style={{ fontSize: 13 }}>{moment(v).format("DD MMM YY HH:mm")}</span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      align: "center",
      fixed: "right",
      render: (_: any, record) => (
        <div className="d-inline-flex gap-2">
          <Button
            size="small"
            type="primary"
            ghost
            icon={<CheckOutlined />}
            onClick={() => onApply(record)}
          >
            Use
          </Button>
          <Tooltip title="Delete template">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => confirmDelete(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div style={{ fontWeight: 600, color: "#0f172a" }}>
            Saved Templates
          </div>
          <div className="small" style={{ color: "#94a3b8" }}>
            Reusable report configurations shared across your company. Use one
            to prefill the Download tab.
          </div>
        </div>
      </div>

      <PayrollDataTable<PayrollTemplate>
        columns={columns}
        dataSource={templates}
        rowKey="id"
        loading={loading}
        scrollX={900}
        emptyText="No templates saved yet. Configure a report in the Download tab and click 'Save as Template'."
        emptyIcon="ti ti-template"
      />
    </div>
  );
};

export default PayrollTemplatesTab;
